import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, ExternalLink, Network, ShieldCheck } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { updates } from '../data/mockData';
import {
  SALAR_GRAPH_PREMIUM_ALT,
  SALAR_GRAPH_PREMIUM_IMAGE,
} from '../assets/salarGraphPremiumImage';

const OPEN_WEIGHTS_HERO = '/img/news/open-weights-governable-execution-2026.svg';

const kindleBooks = [
  {
    key: 'american',
    title: 'The American Arsenal',
    subtitle: 'Defense Electronics Supply-Chain Sovereignty',
    label: 'Primary Release',
    url: 'https://www.amazon.com/dp/B0H743CQQQ',
    coverUrl: 'https://images-na.ssl-images-amazon.com/images/P/B0H743CQQQ.01._SCLZZZZZZZ_SX500_.jpg',
    description: 'Defense electronics, trusted systems, semiconductor supply-chain sovereignty, counterfeit risk, manufacturing stability, and visible supply-chain intelligence.'
  },
  {
    key: 'quantum',
    title: 'Quantum Computing in the Age of Artificial Intelligence',
    subtitle: 'Engineering the Next Civilization',
    label: 'Companion Release',
    url: 'https://www.amazon.com/dp/B0H7F7N5HZ',
    coverUrl: 'https://images-na.ssl-images-amazon.com/images/P/B0H7F7N5HZ.01._SCLZZZZZZZ_SX500_.jpg',
    description: 'Quantum computing, artificial intelligence, probability, security, science, semiconductors, and civilization-scale engineering.'
  }
];

const featuredUpdates = [
  {
    id: 'open-weights-governable-execution-2026-07-24',
    title: 'Recent Release: Open Weights, Governable Execution',
    date: 'July 24, 2026',
    category: 'Technical Publication / Open-Weight AI Security',
    badgeLabel: 'Recent Release',
    cta: 'Read the 8-page public brief',
    url: '/whitepapers#open-weights-governable-execution',
    image: OPEN_WEIGHTS_HERO,
    imageMode: 'contain',
    imageAlt: 'Sovereign open-weight AI accelerator connected to an independent authority plane for identity, policy, tools, budgets, evidence, finality, and emergency control',
    excerpt: 'An 8-page public-safe technical brief separating open-weight model sovereignty from operational authority. It presents a reference architecture for short-lived identity, signed capability leases, default-deny egress, budget ceilings, fresh finality checks, effect-bound receipts, replay resistance, and out-of-band termination—plus worked calculations and a falsifiable conformance framework.'
  },
  {
    id: 'capability-bounded-autonomous-execution-doi-2026-07-23',
    title: 'CBAE Engineering White Paper v2.0 Published with Permanent Zenodo DOI',
    date: 'July 23, 2026',
    category: 'Technical Publication / Agentic AI Security',
    badgeLabel: 'DOI Publication',
    cta: 'View the paper, DOI, RFC, and control cell',
    url: '/whitepapers#capability-bounded-autonomous-execution',
    image: '/img/news/cbae-publication-v2-realistic.webp',
    imageMode: 'cover',
    imageAlt: 'Cinematic CBAE security shield representing independently enforced network, identity, privilege, tools, data, secrets, compute, and external-effect controls',
    excerpt: 'CBAE v2.0 is now permanently published on Zenodo. The report defines explicit authority dimensions, signed short-lived capability leases, use-time enforcement, non-self-modifiable budgets, independent expansion approval, out-of-band termination, tamper-evident evidence, and finality gates. The related secure-design proposal remains under community review as CoSAI Workstream 4 RFC #146.'
  },
  {
    id: 'salar-graph-research-initiative-2026-07-22',
    title: 'Salar Graph Research Initiative Now Published',
    date: 'July 22, 2026',
    category: 'Research Initiative / AI for Science',
    badgeLabel: 'New Research',
    cta: 'Explore Salar Graph',
    url: '/salar-graph',
    image: SALAR_GRAPH_PREMIUM_IMAGE,
    imageMode: 'cover',
    imageAlt: SALAR_GRAPH_PREMIUM_ALT,
    excerpt: 'SALAR Graph is a public-safe AI-for-science initiative connecting reliability engineering, semiconductor materials discovery, graph-based scientific computing, uncertainty scoring, reproducible evidence, and accountable human decision support. The research page presents five graph families, a 24-week roadmap, national-interest alignment, and clear public/private boundaries.'
  },
  {
    id: 'token-economy-whitepaper-2026-07-03',
    title: 'The Token Economy White Paper Added to SALAR Technical White Paper Series',
    date: 'July 3, 2026',
    category: 'Technical White Paper',
    badgeLabel: 'White Paper',
    cta: 'Read and download from Whitepapers',
    url: '/whitepapers#token-economy-whitepaper',
    image: '/img/news/token-economy-realistic.webp',
    imageAlt: 'Cinematic Token Economy research scene with a SALAR token, market chart, and engineering report',
    excerpt: 'SALAR Technologies LLC published The Token Economy, a technical white paper by Vikrant Salar investigating token-based billing from first principles: inference physics, cost per million tokens, the U.S.–China pricing gap, DeepSeek cost structure, the collapse in AI-token pricing, and the case for more transparent metering.'
  },
  {
    id: 'july-4-2026-kindle-books',
    title: 'Fourth of July Update — Two New Kindle Books by Vikrant Salar',
    date: 'July 4, 2026',
    category: 'Publication / Kindle Update',
    badgeLabel: 'Kindle',
    cta: 'View Kindle Books',
    url: '/publications#kindle-books',
    image: '/img/news/salar-july4-kindle-release.svg',
    imageAlt: 'The American Arsenal and Quantum Computing in the Age of Artificial Intelligence displayed as two published books in a realistic study setting',
    excerpt: 'Happy Independence Day. SALAR Technologies LLC is proud to share two Kindle publications by Vikrant Salar: The American Arsenal: Defense Electronics Supply-Chain Sovereignty and Quantum Computing in the Age of Artificial Intelligence: Engineering the Next Civilization. The books reflect current work across trusted electronics, semiconductor resilience, quantum computing, artificial intelligence, supply-chain sovereignty, and the future of engineering.'
  },
  {
    id: 'american-arsenal-kindle-book',
    title: 'The American Arsenal Now Available on Kindle',
    date: 'July 4, 2026',
    category: 'Publication / Kindle',
    badgeLabel: 'Kindle',
    cta: 'Open Kindle Page',
    url: 'https://www.amazon.com/dp/B0H743CQQQ',
    singleBook: kindleBooks[0],
    excerpt: 'The American Arsenal: Defense Electronics Supply-Chain Sovereignty is a strategic engineering atlas by Vikrant Salar covering trusted electronics, resilient manufacturing, semiconductor supply-chain sovereignty, and visible supply-chain intelligence.'
  }
];

function BookCover({ book, compact = false }) {
  return (
    <img
      src={book.coverUrl}
      alt={`${book.title} book cover`}
      className={`shrink-0 rounded-xl border-2 object-cover shadow-2xl bg-[#07101f] ${compact ? 'w-24 h-36' : 'w-32 h-48 md:w-36 md:h-52'}`}
      style={{ borderColor: book.key === 'quantum' ? '#22d3ee' : '#eab84a' }}
      loading="lazy"
    />
  );
}

function Clickable({ item, className = '', children }) {
  const url = item.url || item.targetUrl || item.link || `/news-updates/${item.id}`;
  const external = item.openInNewTab || url.startsWith('http') || /\.(pdf|html)([?#]|$)/i.test(url);

  return external ? (
    <a className={className} href={url} target="_blank" rel="noopener noreferrer">{children}</a>
  ) : (
    <Link className={className} to={url}>{children}</Link>
  );
}

function FallbackVisual({ item }) {
  const isGraph = item.id?.includes('salar-graph');
  const Icon = isGraph ? Network : ShieldCheck;
  return (
    <div className="flex h-full min-h-[220px] w-full flex-col items-center justify-center bg-[radial-gradient(circle_at_center,rgba(34,211,238,0.18),rgba(7,16,31,0.95)_68%)] p-8 text-center">
      <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-3xl border border-cyan-300/40 bg-cyan-400/10 shadow-lg shadow-cyan-950/40">
        <Icon className="h-10 w-10 text-cyan-200" aria-hidden="true" />
      </div>
      <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-200">SALAR Technologies LLC</div>
      <div className="mt-2 max-w-xs text-sm font-semibold text-blue-100">{item.title}</div>
    </div>
  );
}

function VisualMedia({ item, variant = 'tile', eager = false }) {
  const [failed, setFailed] = React.useState(false);
  const sizeClass = variant === 'hero'
    ? 'min-h-[280px] sm:min-h-[340px] lg:min-h-[420px]'
    : variant === 'row'
      ? 'h-52 md:h-full md:min-h-[220px]'
      : 'aspect-[16/10]';

  if (item.singleBook) {
    return (
      <div className={`flex ${sizeClass} items-center justify-center bg-[#06101f] p-7`}>
        <BookCover book={item.singleBook} />
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden bg-[#06101f] ${sizeClass}`}>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_45%,rgba(34,211,238,0.16),rgba(7,16,31,0.92)_72%)]" />
      {failed ? (
        <FallbackVisual item={item} />
      ) : (
        <img
          src={item.image}
          alt={item.imageAlt || item.title}
          className={`relative z-10 h-full w-full transition-transform duration-500 group-hover:scale-[1.015] ${item.imageMode === 'contain' ? 'object-contain' : 'object-cover'}`}
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          onError={() => setFailed(true)}
        />
      )}
    </div>
  );
}

function MetaLine({ item, compact = false }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Badge variant="outline" className="border-cyan-500/40 text-[10px] font-bold uppercase tracking-wider text-cyan-100 sm:text-[11px]">{item.category}</Badge>
      <span className="rounded-full border border-cyan-400/40 bg-cyan-500/10 px-2.5 py-1 text-[9px] font-black uppercase tracking-[0.16em] text-cyan-100 sm:text-[10px]">{item.badgeLabel || 'Update'}</span>
      <div className={`${compact ? '' : 'sm:ml-auto'} flex items-center text-[11px] font-semibold tracking-wide text-cyan-300`}><Calendar size={13} className="mr-1.5" />{item.date}</div>
    </div>
  );
}

function CtaLine({ item }) {
  const url = item.url || item.targetUrl || item.link || `/news-updates/${item.id}`;
  const external = item.openInNewTab || url.startsWith('http') || /\.(pdf|html)([?#]|$)/i.test(url);
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-cyan-300/40 bg-cyan-300/10 px-4 py-2 text-sm font-black text-cyan-100 transition-colors group-hover:border-cyan-200/70 group-hover:bg-cyan-300/15">
      {item.cta || item.ctaLabel || 'Read update'}
      <ArrowRight size={16} />
      {external && <ExternalLink size={13} className="opacity-70" />}
    </span>
  );
}

function LatestReleaseCard({ item }) {
  return (
    <Clickable item={item} className="group block">
      <article className="overflow-hidden rounded-3xl border border-cyan-300/35 bg-[#0b1e35]/95 shadow-2xl shadow-cyan-950/30 ring-1 ring-cyan-300/10 transition-all duration-300 hover:-translate-y-0.5 hover:border-cyan-200/70 hover:shadow-cyan-950/50">
        <div className="grid lg:grid-cols-2">
          <div className="border-b border-cyan-400/20 lg:border-b-0 lg:border-r">
            <VisualMedia item={item} variant="hero" eager />
          </div>
          <div className="flex flex-col justify-center p-7 sm:p-9 lg:p-10">
            <MetaLine item={item} />
            <h2 className="mt-5 text-3xl font-black leading-tight text-white sm:text-4xl">{item.title}</h2>
            <p className="mt-5 text-base leading-7 text-blue-100 sm:text-lg">{item.excerpt || item.body}</p>
            <div className="mt-7"><CtaLine item={item} /></div>
          </div>
        </div>
      </article>
    </Clickable>
  );
}

function FeaturedTile({ item }) {
  return (
    <Clickable item={item} className="group block h-full">
      <article className="flex h-full flex-col overflow-hidden rounded-3xl border border-cyan-500/30 bg-[#0d1f37]/95 shadow-xl shadow-black/20 transition-all duration-300 hover:-translate-y-0.5 hover:border-cyan-200/65 hover:shadow-cyan-950/40">
        <div className="border-b border-cyan-400/20"><VisualMedia item={item} /></div>
        <div className="flex flex-1 flex-col p-6 sm:p-7">
          <MetaLine item={item} compact />
          <h2 className="mt-4 text-2xl font-black leading-snug text-white">{item.title}</h2>
          <p className="mt-4 flex-1 text-sm leading-6 text-blue-100 sm:text-base">{item.excerpt || item.body}</p>
          <div className="mt-6"><CtaLine item={item} /></div>
        </div>
      </article>
    </Clickable>
  );
}

function UpdateRow({ item }) {
  const hasVisual = item.singleBook || item.image;
  return (
    <Clickable item={item} className="group block">
      <article className="overflow-hidden rounded-2xl border border-cyan-500/25 bg-[#0d1f37]/85 shadow-lg shadow-black/10 transition-all duration-300 hover:border-cyan-200/60 hover:bg-[#10243e]">
        <div className={`flex flex-col ${hasVisual ? 'md:flex-row' : ''}`}>
          {hasVisual && (
            <div className="overflow-hidden border-b border-cyan-400/15 bg-[#06101f] md:w-60 md:flex-shrink-0 md:border-b-0 md:border-r">
              <VisualMedia item={item} variant="row" />
            </div>
          )}
          <div className="flex-1 p-6 md:p-7">
            <MetaLine item={item} />
            <h2 className="mt-4 text-xl font-black leading-snug text-white md:text-2xl">{item.title}</h2>
            <p className="mt-3 text-sm leading-6 text-blue-100 md:text-base">{item.excerpt || item.body}</p>
            <div className="mt-5 inline-flex items-center gap-2 text-sm font-black text-cyan-200">
              {item.cta || item.ctaLabel || 'Read update'}<ArrowRight size={16} />
            </div>
          </div>
        </div>
      </article>
    </Clickable>
  );
}

export default function UpdatesPatent9() {
  const civicRelease = updates.find((item) => item.id === 'portland-at-a-crossroads-2026-10-26');
  const aiSelectionRelease = updates.find((item) => item.id === 'choosing-ai-that-lasts-2026-10-26');
  const conservationResearchRelease = updates.find((item) => item.id === 41);
  const legacyStudyRelease = updates.find((item) => item.id === 40);
  const medicalResearchRelease = updates.find((item) => item.id === 39);
  const aiSovereigntyRelease = updates.find((item) => item.id === 38);
  const merged = [
    ...(civicRelease ? [{ ...civicRelease, imageMode: 'cover' }] : []),
    ...(aiSelectionRelease ? [{ ...aiSelectionRelease, imageMode: 'cover' }] : []),
    ...(conservationResearchRelease ? [{ ...conservationResearchRelease, imageMode: 'cover' }] : []),
    ...(legacyStudyRelease ? [{ ...legacyStudyRelease, imageMode: 'cover' }] : []),
    ...(medicalResearchRelease ? [{ ...medicalResearchRelease, imageMode: 'cover' }] : []),
    ...(aiSovereigntyRelease ? [{ ...aiSovereigntyRelease, imageMode: 'contain' }] : []),
    ...featuredUpdates,
    ...updates.filter((item) => item.id !== 'portland-at-a-crossroads-2026-10-26' && item.id !== 'choosing-ai-that-lasts-2026-10-26' && item.id !== 38 && item.id !== 39 && item.id !== 40 && item.id !== 41),
  ];
  const latest = merged[0];
  const highlighted = merged.slice(1, 3);
  const earlier = merged.slice(3);

  return (
    <div className="min-h-screen bg-[#071426] text-white" data-testid="updates-page">
      <Helmet>
        <title>Updates &amp; Insights | SALAR Technologies LLC</title>
        <meta name="description" content="Latest SALAR Technologies publications, open-weight and agentic AI engineering releases, DOI publications, patent filings, public-safe demos, and company announcements." />
        <link rel="canonical" href="https://www.salartechnologies.com/news-updates" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <section className="relative overflow-hidden border-b border-blue-800/30 bg-[#0F1A2B] py-14 sm:py-16">
        <div className="pointer-events-none absolute -left-24 -top-24 h-80 w-80 rounded-full bg-cyan-500/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-40 right-0 h-96 w-96 rounded-full bg-blue-600/10 blur-3xl" />
        <div className="container relative mx-auto max-w-4xl px-6 text-center">
          <Badge variant="outline" className="mb-4 border-cyan-400 text-xs tracking-widest text-cyan-300">NEWS &amp; UPDATES</Badge>
          <h1 className="mb-4 text-4xl font-black tracking-tight sm:text-5xl lg:text-6xl">Updates &amp; Insights</h1>
          <p className="mx-auto max-w-3xl text-base leading-7 text-blue-100 sm:text-lg">Latest engineering releases, public research, patent milestones, demos, publications, and company announcements from SALAR Technologies LLC.</p>
        </div>
      </section>

      <section className="py-12 sm:py-16">
        <div className="container mx-auto max-w-6xl px-5 sm:px-6">
          <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Latest releases</div>
              <h2 className="mt-2 text-2xl font-black text-white sm:text-3xl">Start with the newest SALAR work</h2>
            </div>
            <p className="max-w-xl text-sm leading-6 text-blue-200">The newest release is featured first, followed by current technical publications and research milestones.</p>
          </div>

          <LatestReleaseCard item={latest} />
          {latest.downloadUrl && (
            <div className="mt-5">
              <a href={latest.downloadUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full border border-cyan-300/40 px-5 py-3 font-semibold text-cyan-100 hover:bg-cyan-300/10">
                {latest.downloadLabel || 'Download the white paper'}<ExternalLink size={16} aria-hidden="true" />
              </a>
            </div>
          )}

          <div className="mt-6 grid gap-6 lg:grid-cols-2">
            {highlighted.map((item) => <FeaturedTile key={item.id} item={item} />)}
          </div>

          {earlier.length > 0 && (
            <div className="mt-14 sm:mt-16">
              <div className="mb-6 border-t border-cyan-500/20 pt-8">
                <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Earlier updates</div>
                <h2 className="mt-2 text-2xl font-black text-white sm:text-3xl">Publications, milestones, and company news</h2>
              </div>
              <div className="space-y-5">
                {earlier.map((item) => <UpdateRow key={item.id} item={item} />)}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}


